import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import BecomeSponsorDialog from "@/components/BecomeSponsorDialog";

const HERO_IMG = "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1920&q=80";
const FEATURED_IMG = "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=1400&q=80";
const HANDSHAKE_IMG = "https://images.unsplash.com/photo-1638262052640-82e94d64664a?auto=format&fit=crop&w=1400&q=80";

const STEPS = [
  { n: "01", title: "Apply", copy: "Founders submit their startup. First-come-first-served queue for the selected ones. No warm intros required." },
  { n: "02", title: "Get called", copy: "Shortlisted teams get an email with the live date. Bring proof, not fluff." },
  { n: "03", title: "Pitch live", copy: "10 minutes to pitch and make the case. Then all the time needed for grilling. Unscripted, uncut, publicly witnessed." },
  { n: "04", title: "Walk away", copy: "Yes = €20,000 awarded at investment agreement signature. No = brutal, honest feedback. Both are broadcast." },
];

const FAQS = [
  { q: "Is the €20,000 real?", a: "Yes, coming from the host's own funds. The money is wired by Simone La Torre as soon as the investment agreement into a startup is signed following a Yes decision on the show, and subject to standard KYC and verification that no lies were told by the founder(s) during the streamed session." },
  { q: "What stage do you invest in?", a: "Generally Pre-seed to seed. Live company, revenue not required, but real product traction helps." },
  { q: "Who owns the equity?", a: "Simone La Torre does, at a mutually agreed valuation. Terms are disclosed live for full transparency." },
  { q: "Can I get feedback without pitching live?", a: "No. The whole point is the live pressure. If you can't defend it under fire, I say you can't build it." },
];

function Countdown({ target }) {
  const [days, setDays] = useState(0);
  useEffect(() => {
    const tick = () => {
      if (!target) { setDays(0); return; }
      const diff = Math.max(0, new Date(target).getTime() - Date.now());
      setDays(Math.ceil(diff / 86400000));
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) {
    return (
      <div data-testid="hero-countdown" className="flex items-baseline gap-6">
        <div className="font-heading text-5xl md:text-7xl font-black text-white/40 tabular-nums leading-none">TBD</div>
        <div className="text-xs md:text-sm font-mono uppercase tracking-widest text-white/50">Date to be announced</div>
      </div>
    );
  }
  return (
    <div data-testid="hero-countdown" className="flex items-baseline gap-6">
      <div className="font-heading text-6xl md:text-8xl font-black text-white tabular-nums leading-none">{String(days).padStart(2, "0")}</div>
      <div className="text-xs md:text-sm font-mono uppercase tracking-widest text-white/50">{days === 1 ? "Day" : "Days"} to go</div>
    </div>
  );
}

export default function Landing() {
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState({ episodes: 0, invested: 0 });
  const [nextSessionDate, setNextSessionDate] = useState(null);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/episodes?sort=newest").then(({ data }) => {
      if (data && data.length) setFeatured(data[0]);
      setStats({
        episodes: data?.length || 0,
        invested: (data || []).filter((e) => e.outcome === "invested").length,
      });
    }).catch(() => {});
    api.get("/settings").then(({ data }) => setNextSessionDate(data.next_session_date)).catch(() => {});
    api.get("/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
  }, []);

  const nextLive = nextSessionDate;

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden noise" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} className="w-full h-full object-cover opacity-40" alt="" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-background" />
          <div className="absolute inset-0 grid-lines opacity-40" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse-dot" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/70">On LinkedIn · Unscripted · Uncut</span>
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-[0.9] max-w-5xl">
            Pitch me live.<br />
            Walk away with{" "}
            <span className="text-violet">€20,000</span><br />
            <span className="text-white/60">or brutal honesty.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/70 leading-relaxed">
            No decks in a drawer. No polite maybes. Founders get time on LinkedIn to defend their startup —
            and a real investment decision before they log off.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/apply" data-testid="hero-apply-btn" className="px-8 py-4 bg-lime text-black font-heading font-bold uppercase tracking-wider hover:scale-[1.03] transition-transform inline-flex items-center gap-3">
              Apply to Pitch <span>→</span>
            </Link>
            <Link to="/episodes" data-testid="hero-episodes-btn" className="px-8 py-4 border border-white/20 text-white font-heading font-bold uppercase tracking-wider hover:border-lime hover:text-lime transition-colors">
              Watch Past Episodes
            </Link>
          </div>
          <div className="mt-16 grid grid-cols-3 gap-6 md:gap-10 max-w-2xl">
            <div>
              <div className="font-heading text-4xl md:text-5xl font-black text-violet tabular-nums">€20K</div>
              <div className="text-xs font-mono uppercase tracking-widest text-white/50 mt-2">Fixed ticket size</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-black text-white tabular-nums">{stats.episodes}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-white/50 mt-2">Episodes</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-black text-white tabular-nums">{stats.invested}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-white/50 mt-2">Deals closed</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-black overflow-hidden py-5">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-14 pr-14 font-heading text-2xl md:text-3xl font-black uppercase tracking-tight">
              {["Live on LinkedIn", "€20,000 tickets", "Defend your startup", "Unscripted", "Public due diligence", "Yes or No — live"].map((t, i) => (
                <span key={i} className="flex items-center gap-14">
                  <span className={i % 2 === 0 ? "text-white" : "text-lime"}>{t}</span>
                  <span className="text-white/20">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* UPCOMING LIVE */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-24" data-testid="upcoming-live">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">Next Session</div>
            <h2 className="font-heading text-4xl md:text-5xl font-black uppercase leading-tight tracking-tight">
              One pitch.<br /> One check.<br /><span className="text-lime">Right in your feed.</span>
            </h2>
            <p className="mt-6 text-white/60 text-lg leading-relaxed">
              Tune in on LinkedIn. No sign-up. No paywall. Just real founders under real pressure with real money on the line.
            </p>
          </div>
          <div className="bg-surface border border-white/10 p-8 md:p-12">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-8">Countdown to next drop</div>
            <Countdown target={nextLive} />
            <div className="divider mt-10 mb-6" />
            <a
              href="https://www.linkedin.com/in/simonelatorre/"
              target="_blank"
              rel="noreferrer"
              data-testid="linkedin-follow-btn"
              className="inline-flex items-center gap-3 text-sm font-heading font-bold uppercase tracking-wider hover:text-lime"
            >
              Follow the host on LinkedIn <span>↗</span>
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 border-t border-white/10" data-testid="how-section">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-4">The Format</div>
            <h2 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter">How it works</h2>
          </div>
          <div className="text-white/50 max-w-md text-sm md:text-base">
            Four steps. No fluff. No warm intros required. Just apply, show up, defend the pitch, and take the answer.
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-background p-8 md:p-10 group hover:bg-surface transition-colors" data-testid={`step-${s.n}`}>
              <div className="font-mono text-xs text-violet mb-6">{s.n}</div>
              <div className="font-heading text-2xl md:text-3xl font-black uppercase mb-4 tracking-tight">{s.title}</div>
              <p className="text-white/60 text-sm leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED EPISODE */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-24 border-t border-white/10" data-testid="featured-section">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">Featured Episode</div>
        <div className="grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-3 aspect-video relative overflow-hidden bg-surface">
            <img src={featured?.cover_image_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${featured.cover_image_path}` : FEATURED_IMG} className="w-full h-full object-cover" alt="" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tighter">
                {featured?.startup_name || "Coming soon"}
              </div>
              <div className="text-white/70 text-sm md:text-base mt-2 max-w-xl">
                {featured?.one_liner || "Our first episode is being edited. Stay tuned."}
              </div>
            </div>
          </div>
          <div className="md:col-span-2">
            {featured ? (
              <>
                <div className="inline-block px-3 py-1 border border-white/20 font-mono text-[10px] uppercase tracking-widest mb-6">
                  {featured.outcome === "invested" ? "Invested €20K" : featured.outcome === "passed" ? "Passed" : "Follow-up"}
                </div>
                <p className="text-white/70 leading-relaxed text-base md:text-lg mb-8">{featured.long_description || featured.one_liner}</p>
                <Link to={`/episodes/${featured.slug}`} data-testid="featured-watch-btn" className="inline-flex items-center gap-3 font-heading font-bold uppercase tracking-wider text-lime hover:gap-5 transition-all">
                  Watch the episode <span>→</span>
                </Link>
              </>
            ) : (
              <>
                <p className="text-white/70 leading-relaxed text-base md:text-lg mb-8">
                  The library will fill up fast. Every episode ends with a Yes, a No, or a Follow-up — and it's all on the record.
                </p>
                <Link to="/apply" className="inline-flex items-center gap-3 font-heading font-bold uppercase tracking-wider text-lime hover:gap-5 transition-all">
                  Be the first — apply <span>→</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="border-t border-white/10 py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={HANDSHAKE_IMG} className="w-full h-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/20" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-14">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">About the Host</div>
            <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter leading-tight">
              A €20K check.<br /> No committee.<br /><span className="text-lime">One decision.</span>
            </h2>
          </div>
          <div className="text-white/70 text-base md:text-lg leading-relaxed space-y-6">
            <p>Meet <span className="text-white font-semibold">Simone La Torre</span>, Professional Negotiator, Space Engineer, Business Manager, and Angel Investor.</p>
            <p>One investor, one show, one line of capital, and enough impatience to write a check on air.</p>
            <p>If you build fast, ship real, and can defend the numbers, we should talk.</p>
            <p>If you can't, that's probably gonna be an even better episode.</p>
            <p>PITCH.lu is hosted from Luxembourg and streams on LinkedIn.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-[900px] mx-auto px-6 md:px-10 py-24 border-t border-white/10">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">FAQ</div>
        <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-12">Questions we get</h2>
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-heading text-xl md:text-2xl uppercase text-left hover:text-lime hover:no-underline tracking-tight py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/70 text-base leading-relaxed pb-8">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-lime text-black py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <div className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter">Ready to defend it?</div>
            <div className="mt-3 text-black/70 text-lg">Apply now. Queue is first-come, first-served.</div>
          </div>
          <Link to="/apply" data-testid="footer-apply-cta" className="px-10 py-5 bg-black text-lime font-heading font-black uppercase tracking-wider hover:scale-[1.03] transition-transform text-lg">
            Apply to Pitch →
          </Link>
        </div>
      </section>

      {/* SPONSORS */}
      <section id="sponsors" data-testid="sponsors-section" className="border-t border-white/10 py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 gap-14 items-end mb-14">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">Sponsors</div>
              <h2 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
                Backing the<br /><span className="text-lime">next line</span> of founders.
              </h2>
            </div>
            <div className="text-white/60 text-base md:text-lg leading-relaxed">
              Companies and organizations contributing to the PITCH.lu initiative, for the growth of the project and the success of the startups revolving around it.
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10" data-testid="sponsors-grid">
            {Array.from({ length: 12 }).map((_, i) => {
              const s = sponsors[i];
              return s ? (
                <a
                  key={s.id}
                  href={s.website?.startsWith("http") ? s.website : `https://${s.website}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`sponsor-slot-${i}`}
                  className="bg-background aspect-square flex items-center justify-center overflow-hidden group hover:bg-surface transition-colors"
                  title={s.company_name}
                >
                  {s.logo_path ? (
                    <img
                      src={fileUrl(s.logo_path)}
                      alt={s.company_name}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-300"
                    />
                  ) : (
                    <div className="font-heading text-lg font-black uppercase text-white/60 group-hover:text-lime transition-colors text-center px-4">{s.company_name}</div>
                  )}
                </a>
              ) : (
                <div
                  key={`ph-${i}`}
                  data-testid={`sponsor-slot-${i}`}
                  className="bg-background aspect-square flex items-center justify-center p-6 border border-dashed border-white/10"
                >
                  <div className="text-white/20 font-mono text-[10px] uppercase tracking-widest text-center">
                    Slot<br />open
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="text-white/60 text-sm max-w-lg">
              Want to back the greatest startup ecosystem show of the history of the world, and help the startups gaining visibility? Become a Sponsor, for your logo to live here, linking to your site.
            </div>
            <BecomeSponsorDialog />
          </div>
        </div>
      </section>
    </div>
  );
}
